import AuthComponent from "@components/auth/component";
import CustomButton from "@components/buttons/CustomButton";
import ReportFilter from "@components/filters/ReportFilter";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import useGetAllInventoryReport from "@hooks/report/useGetAllInventoryReport";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import React, { useEffect, useRef } from "react";
import { formatFilterDate } from "@utils/general/format-filter-date";
import { useDraggable } from "react-use-draggable-scroll";
import InventoryTable from "@components/report/InventoryTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { castTypeArr } from "@utils/general/cast-type";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";

function InventoryReportPage() {
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const location = useLocation();

  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });

  const {
    inventoryReportError,
    inventoryReportRefetch,
    inventoryReportFetching,
    inventoryReportPaginator,
    inventoryReportData,
  } = useGetAllInventoryReport({
    page,
    limit,
    search: search,
    //@ts-ignore
    from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
    //@ts-ignore
    to_date: filterRef.current?.to_date || formatFilterDate(today),
  });
  // a custom hook that will handle fetch/mutation errors
  useErrorHandling(...castTypeArr<TFailResponse>([inventoryReportError]));

  useEffect(() => {
    console.log();

    if (location.state?.reload) {
      inventoryReportRefetch();
    }
  }, [location.state?.reload]);
  const handleExportClick = () => {
    if (!inventoryReportData) return;

    const exportData: (string | number)[][] = [];
    const warehouses =
      //@ts-ignore
      inventoryReportData.warehouses?.flatMap(
        (group: any) => group.warehouse_list
      ) || [];
    //@ts-ignore
    const reportRows = inventoryReportData.inventory_report || [];

    // Create header rows
    const headerRow1 = ["SKU", "Code", "Description"];
    const headerRow2 = ["", "", ""];

    warehouses.forEach((wh: any) => {
      headerRow1.push(wh.warehouse_name, "", "", "", "");
      headerRow2.push(
        "Qty",
        "Container",
        "Trip Sale",
        "Total Qty",
        "Contract Qty"
      );
    });

    exportData.push(headerRow1);
    exportData.push(headerRow2);

    // Calculate data rows
    reportRows.forEach((item: any) => {
      const row: (string | number)[] = [
        item.unit_name,
        item.product_code,
        item.product_name,
      ];

      warehouses.forEach((wh: any) => {
        const detail =
          item.details?.find((d: any) => d.warehouse_id === wh.warehouse_id) ||
          {};
        row.push(
          detail.qty ?? 0,
          detail.available_container_qty ?? 0,
          detail.total_trip_qty ?? 0,
          detail.available_qty ?? 0,
          detail.total_consignment_qty ?? 0
        );
      });

      exportData.push(row);
    });

    // Compute grand totals
    const totalRow: (string | number)[] = ["Total", "", ""];
    warehouses.forEach((wh: any) => {
      const totals = reportRows.reduce(
        (acc: any, item: any) => {
          const detail = item.details?.find(
            (d: any) => d.warehouse_id === wh.warehouse_id
          );
          if (detail) {
            acc.qty += detail.qty ?? 0;
            acc.container += detail.available_container_qty ?? 0;
            acc.trip += detail.total_trip_qty ?? 0;
            acc.totalQty += detail.available_qty ?? 0;
            acc.contractQty += detail.total_consignment_qty ?? 0;
          }
          return acc;
        },
        { qty: 0, container: 0, trip: 0, totalQty: 0, contractQty: 0 }
      );

      totalRow.push(
        totals.qty,
        parseFloat(totals.container.toFixed(3)),
        totals.trip,
        totals.totalQty,
        totals.contractQty
      );
    });

    exportData.push(totalRow);

    // Export to Excel
    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory Report");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Inventory_Report.xlsx");
  };

  console.log("data", inventoryReportData);
  return (
    <AuthComponent
      action="view"
      module="Report"
      subModule="Inventory Report"
      page
    >
      <>
        {inventoryReportFetching && <LoadingSpinner />}
        <Search setSearch={setSearch} />
        <PageWrapper title="Inventory Report">
          <div className="grid grid-cols-8 justify-evenly">
            <div className="col-span-6">
              {/* @ts-ignore */}
              <ReportFilter
                refetch={inventoryReportRefetch}
                filterRef={filterRef}
              />
            </div>
            <div className=" flex items-center justify-end  mt-2 col-span-2">
              <CustomButton
                title={
                  <div className="flex  gap-3">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                        fill="white"
                      />
                      <path
                        d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                        fill="white"
                      />
                    </svg>
                    Export Excel
                  </div>
                }
                color="bg-[#1E6E42] h-3/6"
                handleClick={handleExportClick}
                className=""
              />
            </div>
          </div>
          <InventoryTable inventoryReportData={inventoryReportData} />
        </PageWrapper>
      </>
    </AuthComponent>
  );
}

export default InventoryReportPage;
